import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError } from "@/lib/api";

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "", product_ids: [], discount_type: "percent", discount_value: 10,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    active: true,
  });

  const load = async () => {
    const [o, p] = await Promise.all([
      api.get("/offers"),
      api.get("/products", { params: { include_drafts: true, limit: 500 } }),
    ]);
    setOffers(o.data);
    setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const toggleProduct = (id) => {
    setForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id],
    }));
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value),
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date + "T23:59:59").toISOString(),
      };
      if (!payload.name || payload.product_ids.length === 0) {
        toast.error("Nome e almeno un prodotto obbligatori");
        return;
      }
      await api.post("/admin/offers", payload);
      toast.success("Offerta creata");
      setCreating(false);
      setForm({ ...form, name: "", product_ids: [] });
      load();
    } catch (e) {
      toast.error("Errore", { description: formatError(e.response?.data?.detail) || e.message });
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare l'offerta?")) return;
    await api.delete(`/admin/offers/${id}`);
    toast.success("Offerta eliminata");
    load();
  };

  const productName = (id) => products.find((p) => p.id === id)?.title || id;

  return (
    <AdminLayout title="Offerte" back="/admin">
      <Button data-testid="new-offer-btn" onClick={() => setCreating(!creating)} className="rounded-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-bold">
        <Plus className="w-5 h-5 mr-1" /> Nuova offerta
      </Button>

      {creating && (
        <div className="mt-4 bg-white border border-border rounded-2xl p-4 space-y-3">
          <div>
            <Label>Nome offerta *</Label>
            <Input data-testid="offer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo sconto</Label>
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                <SelectTrigger data-testid="offer-type" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentuale (%)</SelectItem>
                  <SelectItem value="fixed">Prezzo fisso (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valore</Label>
              <Input data-testid="offer-value" type="number" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>Data inizio</Label>
              <Input data-testid="offer-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>Data fine</Label>
              <Input data-testid="offer-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <Label>Prodotti coinvolti ({form.product_ids.length})</Label>
            <div className="mt-2 max-h-64 overflow-y-auto border border-border rounded-xl p-2 space-y-1">
              {products.map((p) => (
                <label key={p.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded-lg cursor-pointer">
                  <Checkbox
                    data-testid={`offer-product-${p.id}`}
                    checked={form.product_ids.includes(p.id)}
                    onCheckedChange={() => toggleProduct(p.id)}
                  />
                  <span className="text-sm">{p.title} <span className="text-xs text-slate-500">· € {p.price}</span></span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <Label>Attiva</Label>
          </div>
          <Button data-testid="save-offer-btn" onClick={save} className="rounded-full bg-brand-blue text-white font-bold">
            Crea offerta
          </Button>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {offers.map((o) => (
          <div key={o.id} data-testid={`offer-row-${o.id}`} className="bg-white border border-border rounded-2xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading font-bold text-slate-900">{o.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {o.discount_type === "percent" ? `${o.discount_value}%` : `€ ${o.discount_value}`} · dal {o.start_date.slice(0, 10)} al {o.end_date.slice(0, 10)}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  {o.product_ids.length} {o.product_ids.length === 1 ? "prodotto" : "prodotti"}: {o.product_ids.slice(0, 3).map(productName).join(", ")}
                  {o.product_ids.length > 3 && "..."}
                </div>
              </div>
              <button data-testid={`del-offer-${o.id}`} onClick={() => del(o.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {offers.length === 0 && (
          <div className="text-center text-slate-500 py-8">Nessuna offerta attiva</div>
        )}
      </div>
    </AdminLayout>
  );
}
